import './plp-content-injection.css';

/* eslint-disable no-param-reassign */
export default (element, data) => {
  const { showOnAllPages, isXgenProductGrid } = data;

  const selectors = {
    xgen: {
      productCard: data.layout.isAboveBreakpoint('lg')
        ? '.xgen-product-grid-wrapper > [class*="lg:xgen-grid"] > .xgen-product-card'
        : '.xgen-product-grid-wrapper > [class*="lg:xgen-hidden"] > .xgen-product-card',
      collectionProductCards: '.xgen-product-card',
      imageContainer: '.xgen-image-container img',
    },
    nosto: {
      productCard: '#nosto-collection-target article',
      collectionProductCards: 'article',
      imageContainer: data.layout.isAboveBreakpoint('lg')
        ? '.desktop-image'
        : '.product-images img',
    },
  };

  const getXGenShadowRoot = () => {
    const shadowHost = document.querySelector('x-collection-block');
    return shadowHost?.shadowRoot;
  };

  const injectStylesIntoShadowDOM = async () => {
    const shadowRoot = getXGenShadowRoot();
    if (!shadowRoot) return;

    try {
      const response = await fetch(window.THEME_CSS_URLS.main);
      const cssText = await response.text();

      let styleElement = shadowRoot.querySelector('#core-theme-styles');

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = '#core-theme-styles';
        shadowRoot.appendChild(styleElement);
      }

      styleElement.textContent = cssText;
    } catch (error) {
      console.warn(
        'Could not load CSS file to inject into XGen Shadow DOM.',
        error,
      );
    }
  };

  const init = () => {
    window.addEventListener('productCardsRendered', (e) => {
      setTimeout(() => {
        const currentUrl = new URL(window.location.href);
        const pageParamArray = [...currentUrl.searchParams.entries()].find(
          ([key]) => key.includes('page') || key.includes('filter'),
        );

        let pageParam = (pageParamArray && pageParamArray[1]) || '';

        if (isXgenProductGrid) {
          pageParam =
            e?.detail?.page || (pageParamArray && pageParamArray[1]) || '';
        }

        if (typeof injectMarketingCards === 'function') {
          injectMarketingCards({ pageParam });
        }

        if (typeof injectSections === 'function') {
          injectSections({ pageParam });
        }
      }, 300);
    });
    window.addEventListener('resize', adjustHeight);

    if (isXgenProductGrid) {
      injectStylesIntoShadowDOM();
    }
  };

  const adjustHeight = () => {
    let productCard;
    let allMarketingCards;

    if (isXgenProductGrid) {
      const shadowRoot = getXGenShadowRoot();

      productCard = shadowRoot?.querySelector(selectors.xgen.productCard);
      allMarketingCards = shadowRoot?.querySelectorAll(
        '[data-single-marketing-card]',
      );
    } else {
      productCard = document.querySelector(
        '.nosto-product-grid .nosto-product-card',
      );

      allMarketingCards = document.querySelectorAll(
        '[data-single-marketing-card]',
      );
    }

    let productCardImage = false;

    if (!productCard) {
      productCard = document.querySelector(
        '[data-section="product-grid"] .product-card',
      );
      productCardImage = data.layout.isAboveBreakpoint('lg')
        ? productCard.querySelector('.image')
        : productCard.querySelector('.image');
    } else if (isXgenProductGrid) {
      productCardImage = productCard.querySelector(
        selectors.xgen.imageContainer,
      );
    } else {
      productCardImage = productCard.querySelector(
        selectors.nosto.imageContainer,
      );
    }

    if (!productCard) return; // if product card does not exist, exit function

    const productCardChildElements = productCard.children;

    let productCardHeight = 0;
    [...productCardChildElements].forEach((el) => {
      productCardHeight += el.clientHeight;
    });

    if (!productCardImage) return;

    const productCardImageHeight = productCardImage.clientHeight;

    allMarketingCards.forEach((marketingCard) => {
      const marketingCardContent = marketingCard.querySelector(
        '.marketing-card__content',
      );

      if (marketingCardContent) {
        const mCardImage = marketingCard.querySelector('img');

        if (mCardImage) {
          if (marketingCard.classList.contains('marketing-card--true')) {
            marketingCard.style.height = `${productCardHeight}px`;
          } else {
            marketingCard.style.height = `${productCardImageHeight}px`;
          }
        }
      }
    });
  };

  const injectMarketingCards = ({ pageParam }) => {
    const allMarketingCards = document.querySelectorAll('.marketing-card');

    allMarketingCards.forEach((marketingCard) => {
      marketingCard.remove();
    });

    const shadowRoot = getXGenShadowRoot();

    if (shadowRoot) {
      const shadowMarketingCards = shadowRoot.querySelectorAll(
        '.xgen-product-grid-wrapper > [class*="lg:xgen-grid"] > .marketing-card',
      );

      shadowMarketingCards.forEach((marketingCard) => {
        marketingCard.remove();
      });
    }

    if (pageParam && !showOnAllPages) {
      return;
    }

    const marketingCardTemplates = document.querySelectorAll(
      '[data-marketing-card]',
    );

    let firstArticle;

    if (isXgenProductGrid) {
      firstArticle = shadowRoot?.querySelector(selectors.xgen.productCard);
    } else {
      firstArticle = document.querySelector(selectors.nosto.productCard);
    }

    if (!firstArticle) {
      firstArticle = document.querySelector(
        '[data-section="product-grid"] article',
      );
    }

    const collectionContainer = firstArticle.parentElement;
    const collectionContainerSelector = isXgenProductGrid
      ? selectors.xgen.collectionProductCards
      : selectors.nosto.collectionProductCards;
    const productCards = collectionContainer.querySelectorAll(
      collectionContainerSelector,
    );

    let position;
    marketingCardTemplates.forEach((template, index) => {
      // eslint-disable-next-line
      position = data.layout.isAboveBreakpoint('lg')
        ? parseInt(template.dataset.marketingCard, 10)
        : parseInt(template.dataset.marketingCardMobile, 10);

      position -= index + 1;

      const clone = template.content.cloneNode(true);
      const productNode = productCards[position];
      // eslint-disable-next-line
      collectionContainer.insertBefore(clone, productNode);
    });
    adjustHeight();
  };

  const injectSections = ({ pageParam }) => {
    const sectionsToInject = Array.from(
      document.querySelectorAll('[data-editorial-section]'),
    ).filter((section) => section.dataset.editorialSection !== '0');

    const shadowRoot = getXGenShadowRoot();

    if (pageParam && !showOnAllPages) {
      sectionsToInject.forEach((section) => {
        section.classList.add('!hidden');
      });

      if (isXgenProductGrid) {
        const sections = shadowRoot?.querySelectorAll(
          '[data-editorial-section]',
        );

        sections.forEach((section) => {
          section.classList.add('!hidden');
        });
      }
    } else {
      let firstArticleInGrid;

      if (isXgenProductGrid) {
        firstArticleInGrid = shadowRoot?.querySelector(
          selectors.xgen.productCard,
        );
      } else {
        firstArticleInGrid = document.querySelector(
          selectors.nosto.productCard,
        );
      }

      if (!firstArticleInGrid) {
        firstArticleInGrid = document.querySelector(
          '[data-section="product-grid"] article',
        );
      }
      const collectionContainer = firstArticleInGrid.parentElement;

      sectionsToInject.forEach((section) => {
        collectionContainer.appendChild(section);
        section.classList.remove('hidden', '!hidden');
        section.classList.add('!opacity-100');
      });
    }
  };

  init();
};
